// Mirrors backend/reports_studio/graph/model.py

export type NodeKind =
  | "trigger"
  | "report"
  | "param"
  | "input"
  | "context"
  | "state"
  | "dependency"
  | "compare"
  | "boolean"
  | "temporal"
  | "expr"
  | "branch"
  | "loop"
  | "snapshot"
  | "control"
  | "event"
  | "custom";

export type EdgeKind = "data" | "control" | "report" | "depends";

export interface SourceRange {
  line: number;
  col: number;
  end_line: number;
  end_col: number;
}

export interface GraphNode {
  id: string;
  kind: NodeKind;
  /** How the code reads: ">= 85", "held_for 3 s", "and". */
  label: string;
  /** Plain-language reading ("ENG1n1 is at least 85"); null when the label is already the clearest. */
  text?: string | null;
  trigger?: string | null;
  phase?: string | null;
  parent?: string | null;
  range?: SourceRange | null;
  runtime_kind?: string | null;
  traced: boolean;
  params: Record<string, unknown>;
  badge?: string | null;
  source?: string | null;
  reads: string[];
  writes: string[];
}

export interface GraphEdge {
  id: string;
  source: string;
  target: string;
  kind: EdgeKind;
  label?: string | null;
}

export interface TriggerInfo {
  name: string;
  kind: "function" | "class";
  reports: string[];
  depends_on: string[];
  end_of_flight?: boolean | null;
  range: SourceRange;
  phases: Record<string, SourceRange>;
  parameters: string[];
  dynamic_parameters: boolean;
  /** First paragraph of the trigger's docstring. */
  description?: string | null;
}

/** Text shown on a node: the plain-language reading when available and enabled, else the code label. */
export function nodeText(n: GraphNode, plain: boolean): string {
  return plain && n.text ? n.text : n.label;
}

/** Short human name for a node kind (legend, inspector pill). */
export const KIND_NAMES: Record<NodeKind, string> = {
  trigger: "trigger",
  report: "report",
  param: "parameter",
  input: "setting",
  context: "flight context",
  state: "memory",
  dependency: "other trigger",
  compare: "check",
  boolean: "combine",
  temporal: "timing",
  expr: "math",
  branch: "decision",
  loop: "repeat",
  snapshot: "snapshot",
  control: "flow",
  event: "event",
  custom: "custom code",
};

export interface TriggerGraph {
  file: string;
  triggers: TriggerInfo[];
  nodes: GraphNode[];
  edges: GraphEdge[];
  warnings: string[];
  errors: { line: number; col: number; message: string }[];
}

/** Per-node overlay values (from the evaluation trace at the scrubbed second). */
export type GraphOverlay = Record<string, boolean | number | string | null>;

export function rangeContains(r: SourceRange, line: number, col: number): boolean {
  if (line < r.line || line > r.end_line) return false;
  if (line === r.line && col < r.col) return false;
  if (line === r.end_line && col > r.end_col) return false;
  return true;
}

function rangeSize(r: SourceRange): number {
  const lines = r.end_line - r.line;
  return lines * 1_000_000 + (lines === 0 ? r.end_col - r.col : 1_000_000);
}

/** Smallest node whose source range contains the cursor (same rule as TriggerGraph.node_at). */
export function nodeAt(graph: TriggerGraph, line: number, col: number): GraphNode | null {
  let best: GraphNode | null = null;
  for (const n of graph.nodes) {
    if (!n.range || !rangeContains(n.range, line, col)) continue;
    if (n.kind === "trigger") continue; // the whole definition; never the most useful hit
    if (!best || rangeSize(n.range) < rangeSize(best.range!)) best = n;
  }
  return best;
}
